import React from "react";
import { Leaf } from "lucide-react";
import { useTheme } from "../../context/themeContext";

// ---------------------------------------------------------------------------
// Shared tokens + building blocks used by TestAnalytics, TherapistAnalytics,
// and any page that combines them. Keep this file free of page-specific
// logic (data fetching, chart data, etc.) so both pages can import it
// independently without pulling in the other page's code.
// ---------------------------------------------------------------------------

export const MOOD_ICON = { Low: "🙁", Okay: "😐", Good: "🙂", Excellent: "😄" };
export const RANGE_YEAR = { W: "2026", M: "2026", Y: "" };

export const BRAND = {
  gradient: "linear-gradient(90deg, #ef5da8 0%, #8b7cf6 100%)",
  gradientSoft: "linear-gradient(90deg, #ef5da822 0%, #8b7cf622 100%)",
  navy: "#3d3a6b",
  teal: "#34d9b8",
  pink: "#ef5da8",
  purple: "#8b7cf6",
};

export const TOKENS = {
  dark: {
    isDark: true,
    pageWash:
      "bg-[radial-gradient(circle_at_15%_0%,rgba(139,124,246,0.10),transparent_45%),radial-gradient(circle_at_85%_10%,rgba(239,93,168,0.08),transparent_40%)]",
    card: "bg-[#13152c]/90 border-white/[0.06]",
    cardShadow:
      "shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_20px_50px_-20px_rgba(0,0,0,0.65)]",
    textPrimary: "text-white",
    textMuted: "text-slate-400",
    textFaint: "text-slate-500",
    surface: "bg-white/[0.04]",
    surfaceSoft: "bg-white/[0.03]",
    surfaceHover: "hover:bg-white/[0.06]",
    track: "bg-white/[0.06]",
    pillBorder: "border-white/10",
    gridStroke: "rgba(255,255,255,0.07)",
    axisTick: "#8790ad",
    tooltipBg: "#1d2144",
    tooltipBorder: "rgba(255,255,255,0.1)",
    donutRing: "rgba(255,255,255,0.05)",
    logoWordDark: "text-white",
  },
  light: {
    isDark: false,
    pageWash:
      "bg-[radial-gradient(circle_at_10%_-5%,rgba(239,93,168,0.10),transparent_42%),radial-gradient(circle_at_90%_0%,rgba(139,124,246,0.12),transparent_42%)]",
    card: "bg-white/95 border-black/[0.06]",
    cardShadow:
      "shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_20px_44px_-24px_rgba(31,41,74,0.22)]",
    textPrimary: "text-slate-900",
    textMuted: "text-slate-500",
    textFaint: "text-slate-400",
    surface: "bg-black/[0.035]",
    surfaceSoft: "bg-black/[0.03]",
    surfaceHover: "hover:bg-black/[0.045]",
    track: "bg-black/[0.07]",
    pillBorder: "border-black/10",
    gridStroke: "rgba(15,23,42,0.08)",
    axisTick: "#7c8299",
    tooltipBg: "#ffffff",
    tooltipBorder: "rgba(15,23,42,0.08)",
    donutRing: "rgba(15,23,42,0.05)",
    logoWordDark: "text-[#3d3a6b]",
  },
};

// glow-on-hover per accent, used by CardShell
export const ACCENT_GLOW = {
  purple: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(139,124,246,0.45)] hover:border-[#8b7cf6]/30",
    light:
      "hover:shadow-[0_24px_50px_-20px_rgba(139,124,246,0.35)] hover:border-[#8b7cf6]/30",
  },
  teal: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(52,217,184,0.4)] hover:border-[#34d9b8]/30",
    light:
      "hover:shadow-[0_24px_50px_-20px_rgba(52,217,184,0.3)] hover:border-[#34d9b8]/30",
  },
  pink: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(239,93,168,0.4)] hover:border-[#ef5da8]/30",
    light:
      "hover:shadow-[0_24px_50px_-20px_rgba(239,93,168,0.3)] hover:border-[#ef5da8]/30",
  },
};

// ---------------------------------------------------------------------------
// Brand building blocks — logo mark, wordmark, gradient CTA, laurel accent
// ---------------------------------------------------------------------------

export function InfihealLogoMark({ size = 40 }) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-2xl"
      style={{
        width: size,
        height: size,
        background:
          "linear-gradient(135deg, #34d9b8 0%, #8b7cf6 55%, #ef5da8 100%)",
      }}
    >
      <span
        className="font-extrabold text-white"
        style={{ fontSize: size * 0.5, lineHeight: 1 }}
      >
        i
      </span>
    </div>
  );
}

export function InfihealWordmark() {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  return (
    <div className="flex items-center gap-3">
      <InfihealLogoMark size={38} />
      <div className="flex flex-col leading-none">
        <span className="flex items-baseline text-xl font-extrabold tracking-tight">
          <span className={t.logoWordDark}>infi</span>
          <span style={{ color: BRAND.teal }}>heal</span>
        </span>
        <span
          className={
            "mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] " +
            t.textFaint
          }
        >
          HR Dashboard
        </span>
      </div>
    </div>
  );
}

export function GradientButton({ children, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_-12px_rgba(139,124,246,0.6)] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
      style={{ background: BRAND.gradient }}
    >
      {Icon ? <Icon size={15} strokeWidth={2.5} /> : null}
      {children}
    </button>
  );
}

// A tiny laurel-wreath flourish, echoing the leaf motifs around the
// "1 Million+ / 4.8 / 165+" stats on the infiheal.com hero.
export function LaurelLeaf({ flip = false, color }) {
  return (
    <Leaf
      size={16}
      strokeWidth={2}
      style={{
        color,
        opacity: 0.55,
        transform: flip ? "scaleX(-1) rotate(15deg)" : "rotate(-15deg)",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Small shared building blocks
// ---------------------------------------------------------------------------

export function StatPill({ icon: Icon, tint, value, label }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  return (
    <div
      className={
        "group flex items-center gap-4 rounded-2xl border px-5 py-4 transition-all duration-300 hover:-translate-y-0.5 " +
        t.card +
        " " +
        t.cardShadow
      }
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 20px 45px -20px ${tint}66`;
        e.currentTarget.style.borderColor = `${tint}55`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "";
      }}
    >
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
        style={{ backgroundColor: `${tint}22`, color: tint }}
      >
        <Icon size={20} strokeWidth={2} />
      </div>
      <div>
        <div className="flex items-center gap-1.5">
          <LaurelLeaf color={tint} />
          <span className={"text-2xl font-bold leading-none " + t.textPrimary}>
            {value}
          </span>
          <LaurelLeaf flip color={tint} />
        </div>
        <div className={"mt-1 text-xs " + t.textMuted}>{label}</div>
      </div>
    </div>
  );
}

export function CardShell({ children, className = "", accent = "purple" }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const glow = ACCENT_GLOW[accent][isDark ? "dark" : "light"];
  return (
    <div
      className={
        "rounded-3xl border p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 " +
        t.card +
        " " +
        t.cardShadow +
        " " +
        glow +
        " " +
        className
      }
    >
      {children}
    </div>
  );
}